import { Component, Prop, h, Element, Build } from '@stencil/core';

const COMPONENT_NAME = 'company-logos-type-1';

/**
 * Company logos section with three display variants: default row, side-by-side column, or animated infinite carousel.
 * Logos are rendered with Schema.org Organization microdata. Carousel variant auto-scrolls on client-side load.
 * Versatile logo showcase supporting various layout needs from simple rows to animated carousels.
 *
 * @category Company Logos
 * @slot heading - {required}, <me-text variant="medium" weight="bold" tag="div">
 * @example
 *   <company-logos-type-1 variant="default" logos='[{"src": "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg", "alt": "Microsoft"}, {"src": "https://upload.wikimedia.org/wikipedia/commons/a/a6/Logo_NIKE.svg", "alt": "Nike"}, {"src": "https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg", "alt": "IBM"}, {"src": "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg", "alt": "Google"}]'>
 *     <me-text slot="heading" variant="medium" weight="bold" tag="div">Our clients</me-text>
 *   </company-logos-type-1>
 */
@Component({
  tag: 'company-logos-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class CompanyLogosType1 {
  @Element() el: HTMLElement;

  /**
   * Display variant: default, column, or carousel.
   * @default default
   * @type {string}
   * @enum ['default', 'column', 'carousel']
   */
  @Prop() variant: 'default' | 'column' | 'carousel' = 'default';

  /**
   * List of partner or client logos to display in the section.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {image} src - Logo image source URL (SVG or PNG recommended)
   * @item.property {string} alt - Accessible alt text describing the company or brand
   * @item.required src
   * @item.required alt
   * @item.content alt
   * @minItems 4
   */
  @Prop() logos!: any[];

  /**
   * Enable/disable carousel autoplay
   * @default true
   */
  @Prop() autoplay: boolean = true;

  private scrollContainer?: HTMLDivElement;
  private repeatCount: number = 2;

  componentDidLoad() {
    if (!Build.isBrowser) return;

    if (this.variant === 'carousel' && this.autoplay && this.scrollContainer) {
      // Set the repeat count as a CSS variable so the keyframe animation knows
      // how far to translate to move exactly one copy's width (= 100% / repeatCount)
      this.scrollContainer.style.setProperty('--logos-repeat-count', String(this.repeatCount));
      this.scrollContainer.classList.add(`${COMPONENT_NAME}__list--animated`);
    }
  }

  render() {
    const hasSideLabel = this.variant === 'column';
    const isCarousel = this.variant === 'carousel';
    const isDefault = this.variant === 'default';
    const logosToRender = Array.isArray(this.logos) ? this.logos : [];
    if (!logosToRender.length) return null;

    // Repeat logos so one copy is wide enough to fill even large viewports.
    // Store repeatCount so componentDidLoad can pass it to the CSS animation.
    let repeatedLogos = logosToRender;
    if (isCarousel && logosToRender.length > 0) {
      const minLogos = 20;
      this.repeatCount = Math.max(4, Math.ceil(minLogos / logosToRender.length) * 2);
      repeatedLogos = Array(this.repeatCount).fill(logosToRender).flat();
    }

    return (
      <me-section class={`${COMPONENT_NAME} ${COMPONENT_NAME}--${this.variant}`} yGap="3xl" yGapLg="6xl" role="region" aria-label="Company Logos" xGap="lg" xGapLg="5xl">
        <me-row yGap="2xl" yGapLg={isCarousel ? '4xl' : '2xl'} align={'center'}>
          <me-column span="12" spanLg={hasSideLabel ? '4' : '12'} class={isCarousel || isDefault ? 'company-logos-type-1__text-centered' : undefined}>
            <slot name="heading"></slot>
          </me-column>
          <me-column span="12" spanLg={hasSideLabel ? '8' : '12'}>
            {!Build.isBrowser ? (
              <div class={`${COMPONENT_NAME}__list`}></div>
            ) : (
              <div class={`${COMPONENT_NAME}__list`} ref={el => (this.scrollContainer = el as HTMLDivElement)}>
                {repeatedLogos.map((logo, idx) => (
                  <div class={`${COMPONENT_NAME}__list-item`} key={idx} itemScope itemType="https://schema.org/Organization">
                    <me-logo src={logo.src} alt={logo.alt} itemProp="logo" lazy={true} />
                    <meta itemProp="name" content={logo.alt} />
                    {logo.link && <meta itemProp="url" content={logo.link} />}
                  </div>
                ))}
              </div>
            )}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
